import React, {
    useState,
    useEffect,
    Component,
    useMemo,
    useCallback,
} from "react";
import {
    Box,
    Flex,
    Text,
    SimpleGrid,
    useColorModeValue,
} from "components-lib";
import ComplexTable from "views/admin/default/components/ComplexTable";
import { appointmentcolumnsDataComplex } from "views/admin/default/variables/columnsData";
import { useLocation } from "react-router-dom";
import { API_ENDPOINTS, axiosInstance } from "config/api";
import EditAppointmentModal from "views/admin/appointments/EditAppointmentModal";
import { useGlobalFilters } from "contexts/GlobalFiltersContext";

// Custom hook for URL query parameters
const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

// Add new function to update appointment status
// Removed unused function updateAppointmentStatus

// Add Error Boundary Component
class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error("Error caught by boundary:", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div>Something went wrong. Please try refreshing the page.</div>
            );
        }
        return this.props.children;
    }
}

// Create a separate component for status updates
// Removed unused component StatusUpdater

// Custom hook for handling appointment status updates
const useAppointmentStatus = (selectedDoctor, fromDate, toDate) => {
    const [appointments, setAppointments] = useState([]);

    // Fallback: if no specific doctor selected (undefined or 'all'), try to read doctor id from localStorage.
    // Support a few common storage shapes: a direct key like "user .id" or "user.id", or a JSON "user" object with .doctor or .id.
    try {
        if (!selectedDoctor || selectedDoctor !== "all") {
            const rawUser = localStorage.getItem("user");
            if (rawUser) {
                try {
                    const parsed = JSON.parse(rawUser);
                    if (parsed) {
                        // Prefer doctor property if present (object or primitive)
                        if (parsed.doctor) {
                            selectedDoctor = parsed.id;
                        }
                    }
                } catch (e) {
                    // rawUser wasn't JSON — it might itself be an id string
                    if (rawUser.trim());
                }
            }
        }
    } catch (err) {
        console.warn("Error reading fallback doctor from localStorage", err);
    }

    const fetchAppointments = useCallback(async () => {
        try {
            console.log(
                "Fetching appointments for doctor:",
                selectedDoctor,
                "from:",
                fromDate,
                "to:",
                toDate,
            );

            // API: POST /api/appointment/getAppointmentsByDateRangeByDoctor
            // If selectedDoctor is 'all' or falsy, omit doctor_id to request appointments for all doctors
            const payload = {
                doctor_id:
                    selectedDoctor && selectedDoctor !== "all"
                        ? selectedDoctor
                        : undefined,
                from_date: fromDate,
                to_date: toDate,
            };
            if (selectedDoctor && selectedDoctor !== "all")
                payload.doctor_id = selectedDoctor;

            // Do not toggle a loading state here to keep the UI smooth
            const response = await axiosInstance.post(
                "/api/appointment/getAppointmentsByDateRangeByDoctor",
                payload,
            );

            console.log("Fetch appointments response:", response.data);

            if (response.data?.appointments) {
                const formattedAppointments = response.data.appointments.map(
                    (apt) => ({
                        ...apt,
                        created_date: apt.appointment_date,
                        name: apt.patient_name,
                        doctor_name: apt.doctor_name,
                        doctorId: selectedDoctor,
                        phoneNumber: apt.phone_no,
                        treatment_type: apt.appointment_type,
                        booking_time: apt.appointment_time,
                        payment_action:
                            apt.payment_action === "PENDING"
                                ? "Pending"
                                : apt.payment_action === "PAID"
                                    ? "Paid"
                                    : apt.payment_action,
                        status: apt.appointment_status,
                        raw_status: apt.appointment_status,
                        id: apt.id,
                    }),
                );
                console.log("Formatted appointments:", formattedAppointments);
                setAppointments(formattedAppointments);
            } else {
                setAppointments([]);
            }
        } catch (error) {
            console.error("Error fetching appointments:", error);
            setAppointments([]);
        }
    }, [selectedDoctor, fromDate, toDate]);

    const updateStatus = useCallback(
        async (appointmentId, newStatus) => {
            try {
                console.log("Making API call to update status:", {
                    appointmentId,
                    newStatus,
                    endpoint: "/api/appointment/updateApptStatus",
                });

                const response = await axiosInstance.post(
                    "/api/appointment/updateApptStatus",
                    {
                        id: appointmentId,
                        status: newStatus,
                    },
                );

                console.log("Update status API response:", response.data);

                if (response.data) {
                    console.log(
                        "Status update successful, refreshing appointments...",
                    );
                    await fetchAppointments();
                } else {
                    console.log("Status update response was empty");
                }
            } catch (error) {
                console.error(
                    "Error updating status:",
                    error.response || error,
                );
            }
        },
        [fetchAppointments],
    );

    useEffect(() => {
        // Always fetch appointments when date range changes.
        // If selectedDoctor is 'all' or falsy, the payload omits doctor_id so backend returns all doctors.
        if (fromDate && toDate) {
            console.log(
                "Date range changed, fetching appointments (selectedDoctor may be all):",
                selectedDoctor,
                fromDate,
                toDate,
            );
            fetchAppointments();
        }
    }, [fromDate, toDate, selectedDoctor, fetchAppointments]);

    return {
        appointments,
        updateStatus,
        refreshAppointments: fetchAppointments,
        setAppointments,
    };
};

export default function AppointmentTablePage({
    setAppointmentStats,
    patientSearchQuery = "",
}) {
    // Consume global filters
    const { filters } = useGlobalFilters();
    const {
        doctorId,
        fromDate,
        toDate,
        clinicId: globalClinicId,
        roleContext,
    } = filters;
    const { isDoctor: roleIsDoctor, userDoctorId } = roleContext;
    const selectedDoctor =
        doctorId || (roleIsDoctor ? String(userDoctorId) : "");
    const [selectedAppointmentData, setSelectedAppointmentData] =
        useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const menuBg = useColorModeValue("white", "navy.800");
    const query = useQuery();
    const doctor = decodeURIComponent(query.get("doctor"));
    // history not used in this view

    // Get user role and check if user is owner or admin
    // Role & clinic scoping fully managed by GlobalFiltersContext

    // Selected doctor handled by GlobalFiltersContext

    // Use the custom hook
    const { appointments, refreshAppointments, setAppointments } =
        useAppointmentStatus(selectedDoctor, fromDate, toDate);

    // Handle payment status updates forwarded from ComplexTable (socket events).
    // This keeps the local appointments state in sync when payment changes occur
    // elsewhere (another client/frontdesk/backend) and are broadcast to this clinic.
    const handlePaymentStatusUpdate = React.useCallback(
        (payload) => {
            if (!payload || !payload.appointment_id) return;

            setAppointments((prev) =>
                Array.isArray(prev)
                    ? prev.map((apt) => {
                        const aptId =
                            apt.id ?? apt.appointment_id ?? apt.appointmentId;
                        if (
                            String(aptId) === String(payload.appointment_id)
                        ) {
                            // alert(
                            //     "Payment status updated",
                            //     payload.payment_action,
                            // );
                            return {
                                ...apt,
                                // Normalize status naming used in UI
                                payment_action:
                                    payload.status &&
                                        String(payload.status).toUpperCase() ===
                                        "PAID"
                                        ? "Paid"
                                        : (payload.payment_action ??
                                            apt.payment_action),
                                // Prefer explicit receipt field, otherwise try receipt_urls
                                payment_receipt:
                                    payload.receipt ??
                                    (Array.isArray(
                                        payload.payment_receipt_urls,
                                    )
                                        ? payload.payment_receipt_urls[0]
                                        : payload.payment_receipt_urls) ??
                                    apt.payment_receipt,
                                payment_receipt_urls:
                                    payload.payment_receipt_urls ??
                                    apt.payment_receipt_urls,
                                payment_date:
                                    payload.updated_at ?? apt.payment_date,
                            };
                        }
                        return apt;
                    })
                    : prev,
            );
        },
        [setAppointments],
    );

    // Add logging for appointments data
    useEffect(() => {
        console.log(
            "Appointments data:",
            appointments.map((apt) => ({
                id: apt.id,
                status: apt.status,
                raw_status: apt.raw_status,
            })),
        );
    }, [appointments]);

    // Apply filter to appointments (by patient name or mobile number)
    const filteredAppointments = useMemo(() => {
        if (!patientSearchQuery.trim()) return appointments;
        const q = patientSearchQuery.toLowerCase().trim();
        return appointments.filter(
            (a) =>
                (a.name || "").toLowerCase().includes(q) ||
                (a.phoneNumber || "").toString().includes(q),
        );
    }, [appointments, patientSearchQuery]);

    // State for API-based appointment counts

    // // Fetch appointment counts when filters change
    // useEffect(() => { fetchAppointmentCounts(selectedDoctor); }, [selectedDoctor, fetchAppointmentCounts]);

    // Modify the columns to include the status update handler and remove doctor column for doctor view
    const columnsWithHandlers = useMemo(() => {
        // Filter out the DOCTOR column if we're in the doctor's view
        const filteredColumns = doctor
            ? appointmentcolumnsDataComplex.filter(
                (col) => col.Header !== "DOCTOR",
            )
            : appointmentcolumnsDataComplex;

        return filteredColumns.map((col) => {
            if (col.Header === "STATUS") {
                return {
                    ...col,
                    handleStatusChange: async (row, newStatus) => {
                        try {
                            // Only allow BOOKED -> ARRIVED transition via button click
                            const normalizedStatus = String(row.status)
                                .toUpperCase()
                                .trim();
                            if (normalizedStatus !== "BOOKED") {
                                return; // Don't allow button clicks for other statuses
                            }
                            //imporve UI and implement webscoket for real time update
                            console.log("Status transition:", {
                                from: row.status,
                                to: newStatus,
                                rowId: row.id,
                            });

                            const response = await axiosInstance.post(
                                "/api/appointment/updateApptStatus",
                                {
                                    id: row.id,
                                    status: newStatus,
                                },
                            );

                            console.log(
                                "Status update response:",
                                response.data,
                            );

                            if (response.data) {
                                await refreshAppointments();
                            }
                        } catch (error) {
                            console.error(
                                "Error updating appointment status:",
                                error,
                            );
                        }
                    },
                };
            }
            return col;
        });
    }, [refreshAppointments, doctor]);

    // Add logging to verify appointments data
    useEffect(() => {
        console.log("Current appointments:", appointments);
    }, [appointments]);

    // === GLOBAL_FILTERS_APPLY_POINT: APPOINTMENTS ===
    useEffect(() => {
        // Refresh appointments whenever any relevant global filter changes
        refreshAppointments();
    }, [selectedDoctor, fromDate, toDate, globalClinicId, refreshAppointments]);

    // Listen for custom refresh event
    useEffect(() => {
        const handleRefresh = () => {
            refreshAppointments();
        };

        window.addEventListener("refreshAppointments", handleRefresh);
        return () => {
            window.removeEventListener("refreshAppointments", handleRefresh);
        };
    }, [refreshAppointments]);

    // Function to fetch appointment counts from API
    // const fetchAppointmentCounts = useCallback(async (doctorId) => {
    //   try {
    //       if (doctorId && doctorId !== 'all') {
    //     console.log('Fetching appointment counts for doctor:', doctorId);

    //     // Fetch total appointments for doctor
    //     const totalResponse = await axiosInstance.get(`${API_ENDPOINTS.APPOINTMENT.TOTAL_FOR_DOCTOR}?doctor_id=${doctorId}`);
    //     const totalCount = totalResponse.data?.count || 0;

    //     // Fetch incomplete appointments for doctor
    //     // const incompleteResponse = await axiosInstance.get(`${API_ENDPOINTS.APPOINTMENT.DOCTOR_PENDING}?doctor_id=${doctorId}&date=${today}`);
    //     const incompleteResponse = await axiosInstance.get(`${API_ENDPOINTS.APPOINTMENT.DOCTOR_PENDING}?doctor_id=${doctorId}`);
    //     const incompleteCount = incompleteResponse.data?.count || 0;

    //     // Fetch completed appointments for doctor
    //     let completedCount = 0;
    //     try {
    //       // const completedResponse = await axiosInstance.get(`${API_ENDPOINTS.APPOINTMENT.DOCTOR_COMPLETED}?doctor_id=${doctorId}&date=${today}`);
    //       const completedResponse = await axiosInstance.get(`${API_ENDPOINTS.APPOINTMENT.DOCTOR_COMPLETED}?doctor_id=${doctorId}`); // so it will fetch  all the appts of the doctor
    //       completedCount = completedResponse.data?.count || 0;
    //     } catch (error) {
    //       console.warn('Doctor completed endpoint not available, calculating from total and incomplete');
    //       completedCount = Math.max(0, totalCount - incompleteCount);
    //     }

    //     setAppointmentCounts({
    //       total: totalCount,
    //       pending: incompleteCount,
    //       completed: completedCount
    //     });

    //     } else {
    //     // Fallback to local calculation when no doctor is selected
    //     setAppointmentCounts({
    //       total: filteredAppointments.length,
    //       pending: filteredAppointments.filter(apt => apt.raw_status === "BOOKED").length,
    //       completed: filteredAppointments.filter(apt => apt.raw_status === "COMPLETED").length
    //     });
    //     }
    //   } catch (error) {
    //     console.error('Error fetching appointment counts:', error);
    //     // Fallback to local calculation on error
    //     setAppointmentCounts({
    //       total: filteredAppointments.length,
    //       pending: filteredAppointments.filter(apt => apt.raw_status === "BOOKED" || apt.raw_status === "ARRIVED" || apt.raw_status === "MISSED"  ).length,
    //       completed: filteredAppointments.filter(apt => apt.raw_status === "COMPLETED").length
    //     });
    //   }
    // }, [filteredAppointments]);

    // useEffect(() => {
    //   fetchAppointmentCounts();
    // }, [fetchAppointmentCounts]);

    useEffect(() => {
        setAppointmentStats({
            total: filteredAppointments.length,
            pending: filteredAppointments.filter(
                (apt) =>
                    apt.raw_status === "BOOKED" ||
                    apt.raw_status === "ARRIVED" ||
                    apt.raw_status === "IN_PROGRESS" ||
                    apt.raw_status === "MISSED",
            ).length,
            completed: filteredAppointments.filter(
                (apt) => apt.raw_status === "COMPLETED",
            ).length,
        });
    }, [filteredAppointments]);

    // console.log('Appointments global selectedDoctor:', selectedDoctor);
    return (
        <ErrorBoundary>
            <Box
                pt={{ base: "50px", md: "20px", xl: "10px" }}
                w="100%"
                overflowX="auto"
            >
                {/* <Flex align="center" bg={menuBg} borderRadius="lg" p={4} mb={4} gap={8} boxShadow="sm" justifyContent="flex-end">
          <Flex gap={8} align="center">
            <Flex direction="column" align="center">
              <Text fontWeight="bold">Total Appointments</Text>
              <Text fontWeight="bold" color="#22C55E" fontSize="xl">{filteredAppointments.length}</Text>
            </Flex>
            <Flex direction="column" align="center">
              <Text fontWeight="bold">Incomplete Appointments</Text>
              <Text fontWeight="bold" color="#F59E42" fontSize="xl">{appointmentCounts.pending}</Text>
            </Flex>
            <Flex direction="column" align="center">
              <Text fontWeight="bold">Completed Appointments</Text>
              <Text fontWeight="bold" color="#A259FF" fontSize="xl">{appointmentCounts.completed}</Text>
            </Flex>
          </Flex>
        </Flex> */}
                <SimpleGrid columns={1} gap="20px">
                    <ComplexTable
                        columnsData={columnsWithHandlers}
                        tableData={filteredAppointments.map((apt) => ({
                            ...apt,
                            onEdit: (appointmentData) => {
                                console.log(
                                    "Edit action triggered with data:",
                                    appointmentData,
                                );
                                setSelectedAppointmentData(appointmentData);
                                setIsEditModalOpen(true);
                            },
                        }))}
                        setTableDataComplex={setAppointments}
                        isAppointment={true}
                        doctorId={selectedDoctor}
                        onPaymentStatusUpdate={handlePaymentStatusUpdate}
                    />
                </SimpleGrid>

                {selectedAppointmentData && (
                    <EditAppointmentModal
                        isOpen={isEditModalOpen}
                        onClose={() => {
                            setIsEditModalOpen(false);
                            setSelectedAppointmentData(null);
                            // reload the page on modal close to ensure latest data is reflected
                            try {
                                if (
                                    typeof window !== "undefined" &&
                                    window.location &&
                                    typeof window.location.reload === "function"
                                ) {
                                    window.location.reload();
                                }
                            } catch (err) {
                                console.error(
                                    "Error reloading page on EditAppointmentModal close:",
                                    err,
                                );
                            }
                        }}
                        appointment={selectedAppointmentData}
                        onUpdate={(updatedData) => {
                            console.log(
                                "Doctor appointments - handling update with data:",
                                updatedData,
                            );
                            // Update the table data with the edited appointment
                            const updatedTableData = appointments.map(
                                (item) => {
                                    if (
                                        item.id === updatedData.appointment_id
                                    ) {
                                        // Map the updated fields to match the table data structure
                                        const updatedItem = {
                                            ...item,
                                            // Map appointment_date to created_date
                                            created_date:
                                                updatedData.appointment_date,
                                            appointment_date:
                                                updatedData.appointment_date,
                                            // Map appointment_time to booking_time
                                            booking_time:
                                                updatedData.appointment_time,
                                            appointment_time:
                                                updatedData.appointment_time,
                                            // Map appointment_type to treatment_type
                                            treatment_type:
                                                updatedData.appointment_type,
                                            appointment_type:
                                                updatedData.appointment_type,
                                            // Update services
                                            services: updatedData.services,
                                            // Update emergency status
                                            is_emergency:
                                                updatedData.isEmergency,
                                            isEmergency:
                                                updatedData.isEmergency,
                                        };
                                        console.log(
                                            "Doctor appointments - updated item:",
                                            updatedItem,
                                        );
                                        return updatedItem;
                                    }
                                    return item;
                                },
                            );
                            setAppointments(updatedTableData);
                        }}
                    />
                )}
            </Box>
        </ErrorBoundary>
    );
}
